import React from "react";

/**
 * BookExposureGreeks — ONE shared rendering of the live book greeks (Δ$ per 1% SPX,
 * net vega $/vol-pt, net theta $/day) used by BOTH the Risk dashboard (Long-End Vol &
 * Vega) and the Signals dashboard (Intraday Book Exposure), so the same exposure can
 * never show two different numbers on two pages.
 *
 * Single source of truth = GET /api/risk/long_end_vol (live greeks via risk_greeks).
 * Callers pass the already-derived dollar figures; this component only formats + lays out
 * + stamps the freshness. Read-only display; no data fetching here.
 */

export type BookExposureGreeksData = {
  /** $ P&L for a +1% SPX move (net_delta × spot × 0.01). */
  deltaDollarPer1pct: number | null;
  /** $ P&L per +1 vol point (net portfolio vega). */
  vegaPerPoint: number | null;
  /** $ theta decay per calendar day (usually negative). */
  thetaPerDay: number | null;
};

const _isNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

function _usd(x: number | null | undefined, signed = false): string {
  if (!_isNum(x)) return "—";
  const mag = Math.abs(x).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  if (x < 0) return `−${mag}`;
  return signed && x > 0 ? `+${mag}` : mag;
}

function _tone(x: number | null | undefined): string {
  if (!_isNum(x)) return "opacity-60";
  if (x > 0) return "text-up";
  if (x < 0) return "text-down";
  return "opacity-70";
}

function _asOfLabel(asOf?: string | null): string {
  if (!asOf) return "";
  const t = new Date(asOf);
  if (Number.isNaN(t.getTime())) return "";
  return t.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function BookExposureGreeks({
  data,
  asOf,
  live = true,
  showFootnote = true,
  className = "",
}: {
  data: BookExposureGreeksData;
  asOf?: string | null;
  live?: boolean;
  showFootnote?: boolean;
  className?: string;
}) {
  const tiles: Array<{ label: string; value: string; tone: string; sub: string }> = [
    {
      label: "Δ$ per 1% SPX",
      value: _usd(data.deltaDollarPer1pct, true),
      tone: _tone(data.deltaDollarPer1pct),
      sub: "delta dollars per 1% move",
    },
    {
      label: "Net vega ($/vol pt)",
      value: _usd(data.vegaPerPoint),
      tone: _tone(data.vegaPerPoint),
      sub: "per 1 long-end vol point",
    },
    {
      label: "Net theta ($/day)",
      value: _usd(data.thetaPerDay, true),
      tone: _tone(data.thetaPerDay),
      sub: "decay per day",
    },
  ];

  const asOfTxt = _asOfLabel(asOf);

  return (
    <div className={className}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5"
          >
            <div className="text-[10px] uppercase tracking-wide opacity-60 leading-4">{t.label}</div>
            <div className={`text-[16px] font-semibold leading-tight mt-0.5 ${t.tone}`}>{t.value}</div>
            <div className="text-[11px] opacity-60 leading-4 mt-0.5">{t.sub}</div>
          </div>
        ))}
      </div>
      {showFootnote ? (
        <div className="text-[10px] opacity-55 mt-1.5 leading-4">
          {/*
            When the market is shut these numbers are the last settled session's, so the
            wall-clock stamp is suppressed: "as of 06:52" over close-basis greeks reads as
            a fresh tick taken at 06:52, which is the precise thing it is not. Wording
            matches the tape's existing "market closed · as of last close".
          */}
          {live
            ? `Live book greeks${asOfTxt ? ` · as of ${asOfTxt}` : ""}`
            : "Book greeks · as of last close"}
          {" · single source (risk/long_end_vol)"}
        </div>
      ) : null}
    </div>
  );
}

export default BookExposureGreeks;
